import { ApplicationConfig, provideZoneChangeDetection, Provider, EnvironmentProviders } from '@angular/core';
import { provideRouter, withViewTransitions } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { includeBearerTokenInterceptor } from 'keycloak-angular';

import { appRoutes } from './app.routes';
import { keycloakProvider, bearerTokenConfig } from './core/auth/auth.config';
import { errorInterceptor } from './core/http/error.interceptor';
import { provideAuthTesting, type MockKeycloakOptions } from './core/auth/testing';

// ---------------------------------------------------------------------------
// Cypress hand-off: the Cypress mock posts a sentinel on `window` BEFORE the
// bundle evaluates (via `cy.on('window:before:load', ...)` in
// cypress/support/keycloak-mock.ts). When that sentinel is present we swap the
// production `keycloakProvider` (which would `new Keycloak()` and immediately
// redirect to the real SSO server through an APP_INITIALIZER) for the in-DI
// mock factory `provideAuthTesting()`. This keeps the production bootstrap
// path untouched and never imports Cypress code at runtime for real users.
//
// Why this is needed:
//   - keycloak-angular's `provideKeycloak()` calls `new Keycloak(config)`
//     synchronously at module evaluation and registers an APP_INITIALIZER
//     that runs `keycloak.init({ onLoad: 'login-required' })`, which redirects
//     to the realm login page before any route is rendered.
//   - The previous Cypress mock monkey-patched `window.Keycloak` after import,
//     but `keycloak-angular` imports the `Keycloak` class as an ES module
//     resolved at build time by esbuild — the window patch has no effect.
//   - Swapping the DI provider tree BEFORE bootstrap is the only clean way to
//     stop the real client from being constructed at all.
// ---------------------------------------------------------------------------
const CYPRESS_MOCK_FLAG = '__USE_KEYCLOAK_MOCK__';
const CYPRESS_MOCK_OPTIONS_KEY = '__KEYCLOAK_MOCK_OPTIONS__';

function cypressMockEnabled(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const win = window as unknown as Record<string, unknown>;
  return win[CYPRESS_MOCK_FLAG] === true;
}

function cypressMockOptions(): MockKeycloakOptions {
  if (typeof window === 'undefined') {
    return {};
  }
  const win = window as unknown as Record<string, unknown>;
  const raw = win[CYPRESS_MOCK_OPTIONS_KEY];
  return (raw && typeof raw === 'object' ? raw : {}) as MockKeycloakOptions;
}

const authProviders: (Provider | EnvironmentProviders)[] = cypressMockEnabled()
  ? [...provideAuthTesting(cypressMockOptions()), bearerTokenConfig]
  : [keycloakProvider, bearerTokenConfig];

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(appRoutes, withViewTransitions()),

    ...authProviders,

    provideHttpClient(
      withInterceptors([includeBearerTokenInterceptor, errorInterceptor])
    ),
  ],
};
